import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ShieldCheck } from "lucide-react";
import { evaluateFlag } from "@/lib/featureFlags";

interface Claim {
  claim_headline: string; claim_body: string | null; scope_label: string; sample_size: number | null; improvement_pct: number | null;
}

/**
 * Public-safe homepage widget. Renders only when:
 *  - the delivery_confidence_badge feature flag is on, AND
 *  - at least one published, unexpired, guardrail-passing claim exists.
 * RLS on delivery_claim_snapshots enforces the same on the server.
 */
export default function DeliveryConfidenceBadge() {
  const [claim, setClaim] = useState<Claim | null>(null);

  useEffect(() => {
    (async () => {
      const flag = await evaluateFlag("delivery_confidence_badge");
      if (!flag.on) return;
      const { data } = await supabase
        .from("delivery_claims_public" as any)
        .select("claim_headline,claim_body,scope_label,sample_size,improvement_pct")
        .order("published_at", { ascending: false })
        .limit(1);
      const first = (data ?? [])[0] as any;
      if (first) setClaim(first);
    })();
  }, []);

  if (!claim) return null;
  return (
    <section aria-label="Delivery confidence" className="mx-auto max-w-4xl rounded-2xl border bg-card p-6 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="rounded-xl bg-primary/10 p-3 text-primary"><ShieldCheck className="h-6 w-6" /></div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Delivery confidence · {claim.scope_label}</p>
          <h3 className="text-lg font-semibold mt-1">{claim.claim_headline}</h3>
          {claim.claim_body && <p className="text-sm text-muted-foreground mt-1">{claim.claim_body}</p>}
          <p className="text-xs text-muted-foreground mt-2">
            {claim.improvement_pct != null ? `${claim.improvement_pct}% improvement` : "Verified performance"}{claim.sample_size ? ` across ${claim.sample_size} shipments` : ""}. Updated regularly; claim retires if evidence weakens.
          </p>
        </div>
      </div>
    </section>
  );
}
